import { AlertTriangle, ArrowLeftRight, ArrowRight, Check, ChevronRight, MinusCircle, Plane, PlusCircle, Target } from "lucide-react";
import type { Account, DashboardData, EntryPreset, Owner, Transaction } from "../../types";
import type { TransactionFilters } from "../../app/views";
import { accountBalanceAtMonthEnd } from "../../lib/account-balances";
import { reconciliationMonthLabel } from "../../lib/reconciliation";
import { cardStatementStatus, isCard, money, shortName, todayIn } from "../../lib/finance";
import Avatar from "../../components/Avatar";
import AccountAllocationBar from "../../components/AccountAllocationBar";
import Empty from "../../components/Empty";
import TransactionRow from "../../components/TransactionRow";
import Modal from "../../components/Modal";
import { Private } from "../../components/Privacy";
import { getAccountTrip } from "../../lib/trips";
import { goalCurrentSaved } from "../../lib/goals";
import { accountGoalBreakdown } from "../../lib/goal-reserves";
import AccountTripLeftoverBanner from "./AccountTripLeftoverBanner";

// One account: its balance, and for a card, where the statement cycle has got to. Its links set
// the transaction filters before navigating, so "see what went through this account" lands on a
// list already narrowed rather than on everything.

export interface AccountDetailProps {
  account: Account;
  data: DashboardData;
  month?: string;
  owner: Owner | "All";
  selectedStatement: ReturnType<typeof cardStatementStatus> | null;
  setAccountDetail: (account: Account | null) => void;
  setStatementAccount: (account: Account | null) => void;
  setDetail: (t: Transaction | null) => void;
  /** Opens the transactions list with these filters, clearing the rest. */
  showTransactions: (filters: TransactionFilters) => void;
  onAddTransaction: (
    accountId: string,
    type: "Expense" | "Income" | "Transfer",
    prefill?: Partial<EntryPreset>,
  ) => void;
  onOpenTrip?: (tripId: string) => void;
  onOpenGoal?: (goalId: string) => void;
  onRefresh?: () => Promise<unknown>;
}

export default function AccountDetail({
  account: accountDetail, data, month, selectedStatement, setAccountDetail, setStatementAccount,
  setDetail, showTransactions, onAddTransaction, onOpenTrip, onOpenGoal, onRefresh,
}: AccountDetailProps) {
  const historical = !!month && month !== todayIn(data.timezone).slice(0, 7);
  const balance = month ? accountBalanceAtMonthEnd(accountDetail, data.transactions, month) : accountDetail.balance;
  const activity = data.transactions.filter(t => (!month || t.date.startsWith(month))
    && (t.accountId === accountDetail.id || t.toAccountId === accountDetail.id));
  const trip = getAccountTrip(accountDetail, data.trips);
  const isCash = accountDetail.type.toLowerCase() === "cash";
  const isTripWallet = isCash && Boolean(trip);
  const isCompletedTrip = trip?.status === "completed" || trip?.status === "archived";
  const isLeftover = !historical && isTripWallet && isCompletedTrip && accountDetail.balance > 0;
  const accountsWithBalance = (data.accounts || []).map((a) =>
    a.id === accountDetail.id ? { ...a, balance } : a,
  );
  const accountGoals = !isCard(accountDetail)
    ? accountGoalBreakdown(data.goals, accountDetail.id, month, data.transactions, accountsWithBalance)
    : [];
  const totalReserved = accountGoals.reduce((sum, item) => sum + item.reserved, 0);
  const accountCushion = accountGoals.reduce((max, item) => Math.max(max, item.cushionAmount), 0);

  return (
    <Modal
      title={shortName(accountDetail.name)}
      onClose={() => setAccountDetail(null)}
    >
      <div className="detail-hero">
        <Avatar owner={accountDetail.owner} size={48} />
        <p>
          {accountDetail.owner} · {accountDetail.currency} ·{" "}
          {accountDetail.type}
        </p>
        {trip && (
          <button
            type="button"
            className="detail-trip-link"
            onClick={() => {
              setAccountDetail(null);
              onOpenTrip?.(trip.id);
            }}
            title={`View trip: ${trip.name}`}
          >
            <Plane size={13} />
            <span>{trip.name}</span>
            {isCompletedTrip && <span className="subtle-status">Completed</span>}
            <ChevronRight size={12} className="detail-trip-arrow" />
          </button>
        )}
        <strong>
          <Private>{money(isCard(accountDetail) && accountDetail.creditLimit != null ? accountDetail.creditLimit - balance : balance, accountDetail.currency)}</Private>
        </strong>
        <p>
          {isCard(accountDetail)
            ? (accountDetail.creditLimit != null ? "Available credit" : "Amount owed")
            : historical ? "Closing balance" : "Current balance"}
        </p>
        {historical && <p>At the end of {reconciliationMonthLabel(month!)}</p>}
      </div>

      {accountGoals.length > 0 && !isCard(accountDetail) && (
        <section className="account-detail-goals">
          {(totalReserved > 0 || accountCushion > 0) && (
            <AccountAllocationBar
              balance={balance}
              reserved={totalReserved}
              cushion={accountCushion}
              currency={accountDetail.currency}
              variant="detail"
              availableLabel={historical ? "Unallocated at month end" : undefined}
            />
          )}

          <div className="section-heading">
            <h3>{totalReserved > 0 ? "Goals reserving funds" : "Linked goals"}</h3>
            <span className="subtle-tag">{accountGoals.length} {accountGoals.length === 1 ? "goal" : "goals"}</span>
          </div>
          <div className="account-detail-goals-list">
            {accountGoals.map(({ goal, reserved: goalReserved, cushionAmount, monthlyCommitment, cushionStatus }) => {
              const saved = goalCurrentSaved(goal, month);
              const percent = goal.targetAmount > 0
                ? Math.min(100, Math.round((saved / goal.targetAmount) * 100))
                : 0;
              const isAchieved = saved >= goal.targetAmount || goal.status === "achieved";

              return (
                <button
                  key={goal.id}
                  type="button"
                  className="account-detail-goal-card"
                  onClick={() => {
                    setAccountDetail(null);
                    onOpenGoal?.(goal.id);
                  }}
                  title={`Open goal: ${goal.name}`}
                >
                  <div className="account-goal-card-top">
                    <div className="account-goal-card-identity">
                      <div className="goal-card-icon">
                        <Target size={16} />
                      </div>
                      <div className="account-goal-card-names">
                        <strong className="account-goal-name">{goal.name}</strong>
                        {(goal.floatingGroup || goal.profile) && (
                          <div className="account-goal-tags">
                            {goal.floatingGroup && (
                              <span className="goal-pool-tag">{goal.floatingGroup}</span>
                            )}
                            {goal.profile && (
                              <span className="goal-profile-tag">{goal.profile}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="account-goal-card-earmark">
                      <span className="earmark-label">
                        {goalReserved > 0 ? "Reserved" : "Linked"}
                      </span>
                      <strong className="earmark-value">
                        <Private>
                          {money(
                            goalReserved > 0 ? goalReserved : goal.targetAmount,
                            accountDetail.currency,
                          )}
                        </Private>
                      </strong>
                      <ChevronRight size={14} className="goal-card-arrow" />
                    </div>
                  </div>

                  <div className="account-goal-progress">
                    <div className="account-goal-progress-bar">
                      <div
                        className="account-goal-progress-fill"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="account-goal-progress-meta">
                      <span className="goal-progress-figures">
                        {saved > 0 ? (
                          <>Saved <Private>{money(saved, goal.currency)}</Private> of <Private>{money(goal.targetAmount, goal.currency)}</Private></>
                        ) : (
                          <>Target <Private>{money(goal.targetAmount, goal.currency)}</Private></>
                        )}
                      </span>
                      <span className="goal-progress-pct">{percent}%</span>
                    </div>
                  </div>

                  {(cushionAmount > 0 || monthlyCommitment > 0 || isAchieved) && (
                    <div className="account-goal-card-foot">
                      <div className="account-goal-foot-meta">
                        {cushionAmount > 0 && (
                          <span>Cushion: <Private>{money(cushionAmount, accountDetail.currency)}</Private></span>
                        )}
                        {cushionAmount > 0 && monthlyCommitment > 0 && (
                          <span className="foot-sep" aria-hidden="true">·</span>
                        )}
                        {monthlyCommitment > 0 && (
                          <span><Private>{money(monthlyCommitment, accountDetail.currency)}</Private>/mo</span>
                        )}
                      </div>

                      {isAchieved && cushionStatus && (
                        <div className="account-goal-foot-status">
                          {cushionStatus.isSafeToSpend ? (
                            <span className="safe-buy-badge">✓ Ready to buy</span>
                          ) : (
                            <span className="cushion-shortfall-badge">Needs cushion</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {!historical && <AccountTripLeftoverBanner
        account={accountDetail}
        trip={trip}
        data={data}
        onAddTransaction={onAddTransaction}
        onRefresh={onRefresh}
        onClose={() => setAccountDetail(null)}
      />}

      {!historical && isCard(accountDetail) && <section className="statement-status">
        {selectedStatement?.state !== "current" && <button className="statement-alert" onClick={() => { setStatementAccount(accountDetail); setAccountDetail(null); }}><AlertTriangle size={18}/><span><strong>{selectedStatement?.state === "stale" ? "Update statement details" : "Statement details needed"}</strong><small>{selectedStatement?.state === "stale" ? "The saved details are at least a month old. Check for a newer statement." : `Missing: ${selectedStatement?.missing.join(", ")}`}</small></span><ChevronRight size={17}/></button>}
        <dl className="statement-values">
          <div><dt>Current owed</dt><dd><Private>{money(Math.max(0, balance), accountDetail.currency)}</Private></dd></div>
          {accountDetail.statementBalance != null && <div className="statement-emphasis"><dt>Statement balance</dt><dd><Private>{money(accountDetail.statementBalance, accountDetail.currency)}</Private></dd></div>}
          {selectedStatement?.remaining != null && <div className="statement-emphasis"><dt>Statement remaining</dt><dd><Private>{money(selectedStatement.remaining, accountDetail.currency)}</Private></dd></div>}
          {accountDetail.minimumPayment != null && <div><dt>Minimum required payment</dt><dd>{money(accountDetail.minimumPayment, accountDetail.currency)}</dd></div>}
          {accountDetail.statementDueDate && <div><dt>Payment due</dt><dd>{accountDetail.statementDueDate.split("-").reverse().join("/")}</dd></div>}
          {accountDetail.creditLimit != null && <div><dt>Credit limit</dt><dd><Private>{money(accountDetail.creditLimit, accountDetail.currency)}</Private></dd></div>}
        </dl>
        {selectedStatement?.remaining === 0 && <p className="statement-paid"><Check size={17}/> Statement paid in full.</p>}
        {selectedStatement && !selectedStatement.overdue && selectedStatement.remaining != null && selectedStatement.remaining > 0 && <p className="statement-reminder">Pay <strong><Private>{money(selectedStatement.remaining, accountDetail.currency)}</Private></strong> by {accountDetail.statementDueDate!.split("-").reverse().join("/")} to avoid rollover interest.</p>}
        {selectedStatement?.overdue && selectedStatement.remaining != null && selectedStatement.remaining > 0 && <p className="statement-reminder"><strong><Private>{money(selectedStatement.remaining, accountDetail.currency)}</Private></strong> remaining — was due {accountDetail.statementDueDate!.split("-").reverse().join("/")}.</p>}
        <button className="text-button statement-edit" onClick={() => { setStatementAccount(accountDetail); setAccountDetail(null); }}>{selectedStatement?.state === "current" ? "Update statement" : "Enter statement details"}</button>
      </section>}
      <div className="account-quick-actions">
        <button onClick={() => onAddTransaction(accountDetail.id, "Expense")}>
          <MinusCircle size={18} /> Expense
        </button>
        <button onClick={() => onAddTransaction(accountDetail.id, "Income")}>
          <PlusCircle size={18} /> Income
        </button>
        <button
          onClick={() =>
            onAddTransaction(
              accountDetail.id,
              "Transfer",
              isLeftover
                ? {
                    amount: String(accountDetail.balance),
                    notes: `Transfer leftover ${accountDetail.currency} from ${trip?.name || "trip"}`,
                  }
                : undefined,
            )
          }
        >
          <ArrowLeftRight size={18} /> Transfer
        </button>
      </div>
      <div className="section-heading">
        <h3>{month ? "Month’s activity" : "Recent activity"}</h3>
        <span className="subtle-tag">{month ? reconciliationMonthLabel(month) : "All months"}</span>
      </div>
      {activity
        .slice(0, 5)
        .map((t) => (
          <TransactionRow
            key={t.id}
            t={t}
            accounts={data.accounts || []}
            payees={data.payees}
            trips={data.trips}
            perspectiveAccountId={accountDetail.id}
            onClick={() => {
              setAccountDetail(null);
              setDetail(t);
            }}
          />
        ))}
      {!activity.length && (
        <Empty
          title={month ? "No activity this month" : "No activity yet"}
          description={month ? "The balance carries forward from earlier months." : "This account’s balance comes from its opening balance."}
        />
      )}
      <button
        className="primary-button"
        onClick={() => {
          // Accounts keeps the selected month; other entry points open the latest activity.
          const latest = (data.transactions || []).find(
            (t) =>
              t.accountId === accountDetail.id ||
              t.toAccountId === accountDetail.id,
          );
          setAccountDetail(null);
          showTransactions({ owner: accountDetail.owner, account: accountDetail.id, month: month || latest?.date.slice(0, 7) });
        }}
      >
        Explore transactions <ArrowRight size={18} />
      </button>
    </Modal>
  );
}
